import { NavLink, Outlet } from 'react-router-dom';
import type { BotStatusDto } from '@rhcst/shared';
import { RH_CHAIN_ID_MAINNET } from '@rhcst/shared';
import { useSession } from '../lib/session.js';
import { InterlockStrip } from './InterlockStrip.js';

/**
 * Authenticated shell.
 *
 * A framed instrument rather than a web page: one bordered panel containing a
 * numbered sidebar, the interlock strip, and the routed screen. The frame is
 * what makes seven routes read as one device.
 *
 * The strip is rendered here, once, for every route — see INVARIANT 5 in
 * `docs/DESIGN_HANDOFF.md`. It is not a page and cannot be navigated away from.
 */

const NAV = [
  { to: '/launches', label: 'Launches' },
  { to: '/pairs', label: 'Pairs' },
  { to: '/watchlist', label: 'Watchlist' },
  { to: '/portfolio', label: 'Portfolio' },
  { to: '/intents', label: 'Intents' },
  { to: '/bot', label: 'Bot' },
  { to: '/system', label: 'System' },
  { to: '/audit', label: 'Audit' },
] as const;

/**
 * The agent console.
 *
 * A plain anchor, not a NavLink: `/agent/` is OpenClaw's own UI, served by nginx
 * behind the same session cookie. It is a different application, so react-router
 * must not try to own the URL — a client-side navigation there would render our
 * 404 fallback instead of leaving the SPA.
 *
 * Marked with an arrow because the interlock strip does not follow you there.
 * Every screen inside this console carries the strip (INVARIANT 5); that one is
 * not our screen, and the visual cue is the honest way to say so.
 */
const AGENT_HREF = '/agent/';

export interface ShellProps {
  status: BotStatusDto | null;
  stale: boolean;
}

export function Shell({ status, stale }: ShellProps) {
  const { user, logout } = useSession();

  const chainId = status?.chain_id ?? RH_CHAIN_ID_MAINNET;

  return (
    <div className="app-frame">
      <div className="console">
        <div className="console-shell">
          <nav className="sidebar" aria-label="Console sections">
            <div>
              <div className="sidebar-brand">
                {/* Static chrome accent (v2) -- the interlock strip below is
                    the one ambient "can this spend money" signal
                    (invariant 5); the logo no longer duplicates it. */}
                <span className="brand-mark" aria-hidden="true" />
                <span className="brand-name">RH·CST</span>
              </div>

              <div className="sidebar-nav">
                {NAV.map((item, index) => (
                  <NavLink key={item.to} to={item.to} className="nav-item">
                    <span className="nav-index" aria-hidden="true">
                      {String(index + 1).padStart(2, '0')}
                    </span>
                    <span className="nav-label">{item.label}</span>
                  </NavLink>
                ))}

                <a
                  href={AGENT_HREF}
                  className="nav-item"
                  title="OpenClaw agent console. Opens a separate application — the interlock strip does not follow you there."
                >
                  <span className="nav-index" aria-hidden="true">
                    {String(NAV.length + 1).padStart(2, '0')}
                  </span>
                  <span className="nav-label">
                    Agent <span className="nav-external" aria-hidden="true">↗</span>
                  </span>
                </a>
              </div>
            </div>

            <div className="sidebar-foot">
              <span className="label label-dim">chain {chainId}</span>
              {user && <span className="data-xs muted">{user.email}</span>}
              <button className="btn btn-mini" type="button" onClick={() => void logout()}>
                Sign out
              </button>
            </div>
          </nav>

          <div className="console-main">
            <InterlockStrip status={status} stale={stale} />
            <main className="screen">
              <Outlet />
            </main>
          </div>
        </div>
      </div>
    </div>
  );
}
